/** A segmented control: one tab stop, a radio group moved with ←/→ (and ↑/↓, Home / End),
 *  in the tour dock switch's look (DESIGN.md "Segmented switch"): a hairline frame (2px
 *  padding, 8px radius) holding 28px segments with a 6px radius. The selected segment
 *  takes the `action.selected` fill, weight 600 and navy text, so the choice never rests on
 *  colour alone. For a pick between a few values that swaps what a block shows (the
 *  self-registration links' Tour-wide | Date-specific) or a small exclusive setting (a
 *  link's verification). */
import { Box, ButtonBase, type SxProps, type Theme } from '@mui/material';
import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  /** The radio's accessible name when the label is not plain text (a count pill). */
  name?: string;
  /** A caption under the label (e.g. "resolves to Verify"). */
  detail?: ReactNode;
}

export interface SegmentedProps<T extends string> {
  /** null = nothing selected (e.g. a custom range is active); the first segment is then the
   *  group's tab stop. */
  value: T | null;
  onChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  /** The group's accessible name (or `labelledBy`). */
  label?: string;
  labelledBy?: string;
  disabled?: boolean;
  /** Equal-width segments filling the row (default), or each as wide as its label. */
  fill?: boolean;
  sx?: SxProps<Theme>;
  /** Marks the group for tests / layout probes. */
  dataAttr?: string;
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  labelledBy,
  disabled = false,
  fill = true,
  sx,
  dataAttr,
}: SegmentedProps<T>) {
  const refs = useRef<Map<T, HTMLButtonElement | null>>(new Map());
  const values = options.map((o) => o.value);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const i = value === null ? -1 : values.indexOf(value);
    let next: T | undefined;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = values[(i + 1) % values.length];
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = values[(Math.max(i, 0) - 1 + values.length) % values.length];
    else if (e.key === 'Home') next = values[0];
    else if (e.key === 'End') next = values[values.length - 1];
    if (next === undefined) return;
    e.preventDefault();
    if (next !== value) onChange(next);
    refs.current.get(next)?.focus();
  };

  return (
    <Box
      role="radiogroup"
      aria-label={label}
      aria-labelledby={labelledBy}
      aria-disabled={disabled || undefined}
      onKeyDown={onKeyDown}
      {...(dataAttr ? { [`data-${dataAttr}`]: '' } : {})}
      sx={[
        {
          display: fill ? 'grid' : 'inline-grid',
          gridAutoFlow: 'column',
          gridAutoColumns: fill ? '1fr' : 'auto',
          gap: '2px',
          p: '2px',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <ButtonBase
            key={o.value}
            ref={(el: HTMLButtonElement | null) => {
              refs.current.set(o.value, el);
            }}
            role="radio"
            aria-checked={selected}
            aria-label={o.name}
            disabled={disabled}
            // The group is one tab stop: the selected segment (the first while none is).
            tabIndex={selected || ((value === null || !values.includes(value)) && o.value === values[0]) ? 0 : -1}
            onClick={() => {
              if (!selected) onChange(o.value);
            }}
            data-segment={o.value}
            sx={{
              minHeight: 28,
              borderRadius: '6px',
              px: 1.25,
              py: o.detail ? 0.5 : 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 0,
              typography: 'body2',
              fontWeight: selected ? 600 : 500,
              color: disabled ? 'text.disabled' : selected ? 'primary.main' : 'text.primary',
              bgcolor: selected ? 'action.selected' : 'transparent',
              '&:hover': selected || disabled ? undefined : { bgcolor: 'action.hover' },
              '&.Mui-focusVisible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
            }}
          >
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
              {o.label}
            </Box>
            {o.detail && (
              <Box
                component="span"
                sx={{ typography: 'caption', color: 'text.secondary', fontWeight: 400, lineHeight: 1.3, textAlign: 'center' }}
              >
                {o.detail}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
